import Head from 'expo-router/head';
import React from 'react';

const BASE_URL = process.env.EXPO_PUBLIC_BASE_URL;

interface MetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  canonical?: string;
  robots?: string;
}

const defaultMeta = {
  title: 'Citation Needed - Find the Paper. Back the Claim.',
  description: 'Find scientific papers referenced in any text or article. Paste content or URL to discover the research behind the claims. Free academic paper discovery tool.',
  keywords: 'scientific papers, academic research, citation finder, paper search, research discovery, academic citations, scholarly articles',
  ogImage: `${BASE_URL}/images/logo-full.jpg`,
  twitterCard: 'summary_large_image' as const,
  robots: 'index, follow',
};

export default function Meta({
  title,
  description,
  keywords,
  ogImage,
  twitterCard,
  canonical,
  robots,
}: MetaProps) {
  const metaTitle = title || defaultMeta.title;
  const metaDescription = description || defaultMeta.description;
  const metaKeywords = keywords || defaultMeta.keywords;
  const metaOgImage = ogImage || defaultMeta.ogImage;
  const metaTwitterCard = twitterCard || defaultMeta.twitterCard;
  const metaRobots = robots || defaultMeta.robots;
  const metaCanonical = canonical || `${BASE_URL}/`;

  return (
    <Head>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={metaRobots} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Open Graph */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaOgImage} />
      <meta property="og:url" content={metaCanonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Citation Needed" />

      {/* Twitter */}
      <meta name="twitter:card" content={metaTwitterCard} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaOgImage} />

      {/* Canonical URL */}
      <link rel="canonical" href={metaCanonical} />

      <link rel="manifest" href="/site.webmanifest" />

      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Citation Needed",
          "description": metaDescription,
          "url": BASE_URL,
          "applicationCategory": "EducationApplication",
          "operatingSystem": "Web, iOS, Android",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "Citation Needed"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": BASE_URL
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
    </Head>
  );
}
